/**
 * Invite-accept landing page (`/accept-invite/:token`).
 *
 * Flow:
 *  1. URL hits this component with a one-time `:token` from the email.
 *  2. If the user isn't signed in, bounce to /signin with a `next` so
 *     they come back here post-auth.
 *  3. Once authenticated, POST the token to the backend which binds
 *     the invite row to the current user, grants any pre-allocated
 *     credits and stamps them as `plan=enterprise`.
 *  4. Toast + redirect into the studio with the new team context.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Check, AlertCircle } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");  // loading | success | failed
  const [workspace, setWorkspace] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/signin?next=${encodeURIComponent(`/accept-invite/${token}`)}`);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/workspaces/invites/${token}/accept`);
        if (cancelled) return;
        setWorkspace(res.data?.workspace);
        setState("success");
        toast.success("Welcome to the team!");
        setTimeout(() => { if (!cancelled) navigate("/app"); }, 1600);
      } catch (err) {
        if (cancelled) return;
        setError(String(err?.response?.data?.detail || "Couldn't accept this invite."));
        setState("failed");
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, token, navigate]);

  return (
    <div className="min-h-screen bg-[#070707] flex items-center justify-center p-4 text-white">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0E0E0E] p-8 text-center" data-testid="accept-invite-card">
        {state === "loading" && (
          <>
            <Loader2 className="w-6 h-6 mx-auto text-[#D4AF37] animate-spin" />
            <div className="font-display text-2xl text-white mt-4">Claiming your seat…</div>
            <p className="text-sm text-white/55 mt-1">Hold tight.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="font-display text-2xl text-white mt-4">
              Welcome to {workspace?.name || "the team"}.
            </div>
            <p className="text-sm text-white/55 mt-1">
              Redirecting to your studio…
            </p>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 border border-rose-400/40 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-rose-300" />
            </div>
            <div className="font-display text-2xl text-white mt-4">Invite issue</div>
            <p className="text-sm text-white/55 mt-1">{error}</p>
            <button
              onClick={() => navigate("/app")}
              data-testid="accept-invite-go-studio"
              className="mt-5 text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black hover:bg-[#F3CE5A] transition"
            >
              Go to studio
            </button>
          </>
        )}
      </div>
    </div>
  );
}
